import { lazy, Suspense } from 'react';
import useTranslation from 'canvas_states/translation/useTranslation';
import useAppConfig from 'canvas_states/config/useAppConfig';
import DocumentHead from '../../molecules/DocumentHead';
import useTermsAndConditionsUrl from 'canvas_states/config/useTermsAndConditionsUrl';
import Header from '../../molecules/Header';
import Footer from '../../molecules/Footer';
import PageLayout from '../../molecules/PageLayout';
import { GoogleAnalyticClick } from '../../../../types/analytics/GoogleAnalyticsEvent';
import useCommmonEvents from '../../utils/analytics/useCommonEvents';
import useCurrentLanguage from 'canvas_states/language/useCurrentLanguage';
import useUrlInfo from '../../utils/urls/useUrlInfo';
import HeaderLanguageCurrencyButton from './components/HeaderLanguageCurrencyButton';
import type Language from '../../../../types/Language';
import HeaderLanguageCurrencyMenu from './components/HeaderLanguageCurrencyMenu';
import useIsMobileScreen from '../../utils/screenSizes/useIsMobileScreen';
import useDevPanel from 'canvas_states/devPanel/useDevPanel';

const DevPanel = lazy(async () => await import('../../organisms/DevPanel'));

export interface PageProps extends React.PropsWithChildren {
  title?: string;
  fallback?: React.ReactNode;
  hideCurrencySelector?: boolean;
  backButton?: React.ReactNode;
  headerButtons?: React.ReactNode;
  bottomBanner?: React.ReactNode;
  topBanner?: React.ReactNode;
}

const Page: React.FC<PageProps> = ({
  title,
  children,
  hideCurrencySelector,
  fallback = null,
  backButton,
  headerButtons,
  bottomBanner,
  topBanner,
}) => {
  const { config } = useAppConfig().states;

  const footerConfig = config.up?.componentConfigs?.plusgradeFooter;

  const {
    states: { termsAndConditionsUrl, termsAndConditionsExtraSeatUrl },
    selectors: { hasDynamicTermsAndConditions },
  } = useTermsAndConditionsUrl();
  const { publishLanguageChange, publishClick, publishOpenDevPanel } = useCommmonEvents().actions;

  const { currentLanguage } = useCurrentLanguage().states;
  const {
    actions: { toggleDevPanel },
    states: { isOpened },
  } = useDevPanel();
  const { pid } = useUrlInfo().states;

  const { loading, initialized } = useTranslation().states;

  const { isMobile } = useIsMobileScreen().states;

  // we should not show loading when translation is not initialized
  if (!initialized) return null;

  const currencyLanguageProps = {
    hideCurrencySelector,
    pid,
    onLanguageChange: (newLanguage: Language) => {
      publishLanguageChange(newLanguage.code);
    },
  };

  const handleLogoDoubleClick = async () => {
    toggleDevPanel();
    if (!isOpened) {
      await publishOpenDevPanel();
    }
  };

  return (
    <PageLayout
      topBanner={topBanner}
      header={
        <Header
          backButton={backButton}
          hamburgerMenu={isMobile && <HeaderLanguageCurrencyMenu {...currencyLanguageProps} />}
        >
          <>
            {!isMobile && <HeaderLanguageCurrencyButton {...currencyLanguageProps} />}
            {headerButtons}
          </>
        </Header>
      }
      footer={
        <>
          <Footer
            partnerName={config.partnerName}
            faqUrl={footerConfig?.faqUrl}
            termsAndConditionsUrl={termsAndConditionsUrl}
            termsAndConditionsExtraSeatUrl={termsAndConditionsExtraSeatUrl}
            hasDynamicTermsAndConditions={hasDynamicTermsAndConditions}
            onTermsClick={() => {
              publishClick(GoogleAnalyticClick.TERMS_AND_CONDITIONS);
            }}
            onPrivacyClick={() => {
              publishClick(GoogleAnalyticClick.PRIVACY_SETTINGS);
            }}
            onLogoDoubleClick={handleLogoDoubleClick}
          />
          {bottomBanner}
        </>
      }
    >
      <DocumentHead
        partnerShortName={config.partnerShortName ?? config.partnerName}
        title={title}
        language={currentLanguage}
      />
      <Suspense fallback={null}>
        <DevPanel />
      </Suspense>
      <Suspense fallback={fallback}>{loading ? fallback : children}</Suspense>
    </PageLayout>
  );
};

export default Page;
